import React, { useRef } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Link,
  Card,
  CardContent,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { motion } from "framer-motion";
import Typewriter from "typewriter-effect";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./App.css";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1976d2" }, // Blue
    secondary: { main: "#ffffff" }, // White
    background: { default: "#f5f7fa" }, // Light gray
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: "4rem",
      "@media (max-width:600px)": { fontSize: "2.5rem" },
    },
    h3: {
      fontWeight: 600,
      fontSize: "2.5rem",
      "@media (max-width:600px)": { fontSize: "1.8rem" },
    },
    h5: {
      fontWeight: 400,
      fontSize: "1.3rem",
      "@media (max-width:600px)": { fontSize: "1rem" },
    },
    body1: {
      fontSize: "1.1rem",
      color: "#555",
      "@media (max-width:600px)": { fontSize: "0.9rem" },
    },
  },
});

const testimonials = [
  {
    name: "Jane D.",
    quote:
      "Shravanology gave me a safe space to express my feelings without judgment.",
  },
  {
    name: "Alex R.",
    quote: "The AI insights helped me understand my emotions better.",
  },
  {
    name: "Sam K.",
    quote: "Real-time translation is a game-changer for connecting globally.",
  },
];

function App() {
  const scrollToSection = (id) => {
    document.getElementById(id).scrollIntoView({ behavior: "smooth" });
  };

  const waitlistRef = useRef(null);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Navbar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          bgcolor: "secondary.main",
          boxShadow: 1,
        }}
      >
        <Container
          sx={{
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" color="primary">
            Shravanology
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              "@media (max-width:600px)": { gap: 1 },
            }}
          >
            {[
              "home",
              "about",
              "how-it-works",
              "technology",
              "testimonials",
              "waitlist",
            ].map((section) => (
              <Button
                key={section}
                color="primary"
                onClick={() => scrollToSection(section)}
                sx={{ fontSize: "0.9rem", "&:hover": { color: "#1565c0" } }}
                component={motion.button}
                whileHover={{ scale: 1.1 }}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </Button>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        id="home"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          bgcolor: "background.default",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(135deg, rgba(25, 118, 210, 0.6), rgba(33, 150, 243, 0.4))",
            zIndex: 1,
          },
        }}
      >
        <Container
          sx={{
            textAlign: "center",
            position: "relative",
            zIndex: 2,
            color: "secondary.main",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <Typography variant="h1" gutterBottom>
              Shravanology
            </Typography>
            <Typography variant="h3" sx={{ mb: 3 }}>
              <Typewriter
                options={{
                  strings: [
                    "Speak your heart. We listen.",
                    "Connect through your voice.",
                    "Empower your emotions with AI.",
                  ],
                  autoStart: true,
                  loop: true,
                  delay: 50,
                }}
              />
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, maxWidth: 700, mx: "auto" }}>
              An anonymous voice-sharing platform powered by AI, helping people
              express their emotions and translate them in real-time for global
              connection and mental wellness.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                "@media (max-width:600px)": { flexDirection: "column" },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => scrollToSection("waitlist")}
                sx={{ px: 4, py: 1.5, "&:hover": { bgcolor: "#1565c0" } }}
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                animate={{
                  y: [0, -10, 0],
                  transition: { duration: 2, repeat: Infinity },
                }}
              >
                Join Waitlist
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" // Placeholder demo video
                sx={{
                  px: 4,
                  py: 1.5,
                  borderColor: "secondary.main",
                  color: "secondary.main",
                  "&:hover": { borderColor: "#e0e0e0", color: "#e0e0e0" },
                }}
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                animate={{
                  y: [0, -10, 0],
                  transition: { duration: 2.2, repeat: Infinity },
                }}
              >
                Watch Demo
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about" sx={{ py: 10, bgcolor: "secondary.main" }}>
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h3"
              color="primary"
              align="center"
              gutterBottom
            >
              About Shravanology
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{ maxWidth: 800, mx: "auto", mb: 4 }}
            >
              Shravanology is a revolutionary platform designed to empower
              individuals to share their emotions anonymously through voice
              messages. Leveraging advanced AI, we transcribe, translate, and
              analyze mood patterns in real-time, fostering mental wellness and
              global connectivity.
            </Typography>
            <Typography
              variant="h5"
              color="primary"
              align="center"
              sx={{ mb: 2 }}
            >
              Why Shravanology?
            </Typography>

            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1} // show 1 card at a time on small screens
              slidesToScroll={1}
              responsive={[
                {
                  breakpoint: 900,
                  settings: {
                    slidesToShow: 1,
                  },
                },
                {
                  breakpoint: 1200,
                  settings: {
                    slidesToShow: 2,
                  },
                },
                {
                  breakpoint: 1536,
                  settings: {
                    slidesToShow: 3,
                  },
                },
              ]}
            >
              {[
                {
                  title: "Anonymity",
                  desc: "Share your voice without revealing your identity.",
                },
                {
                  title: "AI Insights",
                  desc: "Real-time mood detection to support emotional clarity.",
                },
                {
                  title: "Global Reach",
                  desc: "Translate your message into multiple languages instantly.",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      p: 3,
                      bgcolor: "secondary.main",
                      boxShadow: 2,
                      textAlign: "center",
                      mx: 1,
                    }}
                  >
                    <Typography variant="h6" color="primary">
                      {item.title}
                    </Typography>
                    <Typography variant="body2">{item.desc}</Typography>
                  </Card>
                </motion.div>
              ))}
            </Slider>
          </motion.div>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ py: 10, bgcolor: "background.default" }}>
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h3"
              color="primary"
              align="center"
              gutterBottom
            >
              How Shravanology Works
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{ maxWidth: 800, mx: "auto", mb: 6 }}
            >
              Our platform is designed to be intuitive, secure, and impactful.
              Follow these simple steps to share your voice and connect with a
              global audience.
            </Typography>
            <Grid
              container
              spacing={4}
              sx={{ flexWrap: "nowrap", overflowX: "auto" }}
            >
              {[
                {
                  step: "Record Your Voice",
                  desc: "Speak anonymously using our secure audio recorder.",
                  icon: "https://img.icons8.com/color/60/microphone--v1.png",
                },
                {
                  step: "AI Transcription",
                  desc: "Our AI transcribes and translates your message instantly.",
                  icon: "https://img.icons8.com/color/60/artificial-intelligence.png",
                },
                {
                  step: "Mood Analysis",
                  desc: "Real-time sentiment analysis to understand your emotions.",
                  icon: "https://img.icons8.com/color/60/happy--v1.png",
                },
                {
                  step: "Connect",
                  desc: "Join support communities for shared experiences.",
                  icon: "https://img.icons8.com/color/60/groups.png",
                },
              ].map((item, index) => (
                <Grid
                  item
                  key={index}
                  sx={{ flexWrap: "nowrap", width: "100%" }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        bgcolor: "secondary.main",
                        borderRadius: 2,
                        boxShadow: 1,
                        height: "100%",
                      }}
                    >
                      <img
                        src={item.icon}
                        alt={item.step}
                        style={{ width: 60, height: 60, marginBottom: 16 }}
                      />
                      <Typography variant="h6" color="primary">
                        Step {index + 1}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {item.step}
                      </Typography>
                      <Typography variant="body2">{item.desc}</Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>
      {/* Technology Section */}
      <Box id="technology" sx={{ py: 10, bgcolor: "secondary.main" }}>
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h3"
              color="primary"
              align="center"
              gutterBottom
            >
              Built with Cutting-Edge Technology
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{ maxWidth: 800, mx: "auto", mb: 4 }}
            >
              Shravanology is powered by Elastic Stack for advanced logging,
              analytics, and search, hosted on AWS’s robust infrastructure with
              services like EC2, Lambda, S3, Transcribe, and Polly for
              scalability and AI-driven voice processing.
            </Typography>
            <Typography
              variant="h5"
              color="primary"
              align="center"
              sx={{ mb: 4 }}
            >
              Why Elastic Stack and AWS?
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{ maxWidth: 800, mx: "auto", mb: 6 }}
            >
              Elastic Stack enables real-time analytics and insights into user
              interactions, while AWS provides unmatched scalability, security,
              and AI capabilities, ensuring seamless voice data processing and
              global accessibility with minimal latency.
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  name: "Elastic Stack",
                  icon: "https://simpleicons.org/icons/elasticstack.svg",
                },
                {
                  name: "Node.js",
                  icon: "https://simpleicons.org/icons/nodedotjs.svg",
                },
                {
                  name: "React",
                  icon: "https://simpleicons.org/icons/react.svg",
                },
                {
                  name: "MongoDB",
                  icon: "https://simpleicons.org/icons/mongodb.svg",
                },
              ].map((tech, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.1 }}
                  >
                    <Card
                      sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: "secondary.main",
                        boxShadow: 2,
                      }}
                    >
                      <img
                        src={tech.icon}
                        alt={tech.name}
                        style={{ width: 60, height: 60, marginBottom: 8 }}
                      />
                      <Typography variant="h6" color="primary">
                        {tech.name}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ textAlign: "center", mt: 6 }}>
              <a href="https://aws.amazon.com/what-is-cloud-computing">
                <img
                  src="https://d0.awsstatic.com/logos/powered-by-aws-white.png"
                  alt="Powered by AWS Cloud Computing"
                  style={{
                    width: 150,
                    height: "auto",
                    "@media (max-width:600px)": { width: 100 },
                  }}
                />
              </a>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box id="testimonials" sx={{ py: 10, bgcolor: "background.default" }}>
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h3"
              color="primary"
              align="center"
              gutterBottom
            >
              What Users Say
            </Typography>
            <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
              <Slider {...sliderSettings}>
                {testimonials.map((testimonial, index) => (
                  <Box key={index} sx={{ p: 2 }}>
                    <Card
                      sx={{ p: 3, bgcolor: "secondary.main", boxShadow: 2 }}
                    >
                      <Typography
                        variant="body1"
                        align="center"
                        sx={{ mb: 2, fontStyle: "italic" }}
                      >
                        "{testimonial.quote}"
                      </Typography>
                      <Typography variant="h6" color="primary" align="center">
                        {testimonial.name}
                      </Typography>
                    </Card>
                  </Box>
                ))}
              </Slider>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Join Waitlist Form */}
      <Box id="waitlist" sx={{ py: 10, bgcolor: "secondary.main" }}>
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h3"
              color="primary"
              align="center"
              gutterBottom
            >
              Join the Waitlist
            </Typography>
            <Typography variant="body1" align="center" sx={{ mb: 4 }}>
              Be among the first to experience Shravanology. Join 500+ others
              already on the list!
            </Typography>
            <Box sx={{ maxWidth: 400, mx: "auto" }}>
              <form>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <TextField
                    label="Name"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    sx={{ input: { bgcolor: "secondary.main" } }}
                  />
                  <TextField
                    label="Email"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    type="email"
                    sx={{ input: { bgcolor: "secondary.main" } }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2, py: 1.5, "&:hover": { bgcolor: "#1565c0" } }}
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                  >
                    Get Early Access
                  </Button>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ mt: 2, color: "#777" }}
                  >
                    Your privacy is our priority. We’ll never share your
                    information.
                  </Typography>
                </motion.div>
              </form>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        id="contact"
        sx={{ py: 6, bgcolor: "#1565c0", color: "secondary.main" }}
      >
        <Container sx={{ textAlign: "center" }}>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography variant="h3" gutterBottom>
              Get in Touch
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Business Email:{" "}
              <Link 
                href="mailto:sarwan@shravanology.in"
                color="inherit"
                underline="hover"
              >
                sarwan@shravanology.in
              </Link>
            </Typography>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}
            >
              <motion.a
                href="https://x.com"
                target="_blank"
                whileHover={{ scale: 1.2 }}
              >
                <img
                  src="https://simpleicons.org/icons/x.svg"
                  alt="X"
                  style={{
                    width: 30,
                    height: 30,
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </motion.a>
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Interested in partnering?{" "}
              <Link
                href="mailto:sarwan@shravanology.in"
                color="inherit"
                underline="hover"
              >
                Let’s collaborate!
              </Link>
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2">
                © {new Date().getFullYear()} Shravanology. All rights reserved.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Link href="#" color="inherit" underline="hover" sx={{ mx: 1 }}>
                  Privacy Policy
                </Link>
                <Link href="#" color="inherit" underline="hover" sx={{ mx: 1 }}>
                  Terms of Service
                </Link>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
